import ErrorIcon from '@mui/icons-material/Error';
import {
  FormControl,
  Grid,
  ListSubheader,
  MenuItem,
  Select,
  SelectProps,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { useTheme } from 'styled-components';
import { ReactComponent as ExpandArrowIcon } from '../../assets/icons/iconExpandArrow.svg';
import { Container } from './styles';
import { Input } from '../Inputs/Input';

type ProfileViewModel = {
  id?: string;
  name?: string;
};

type Props = SelectProps & {
  errorText?: string;
  placeholderTitle?: string;
  operators?: ProfileViewModel[];
  requesters?: ProfileViewModel[];
  approvers?: ProfileViewModel[];
  financers?: ProfileViewModel[];
  name: string;
  control: any;
  isSearchable?: boolean;
};

export function SelectProfileCollaborator({
  operators,
  requesters,
  approvers,
  financers,
  errorText,
  name,
  placeholderTitle,
  control,
  isSearchable,
  ...props
}: Props) {
  const theme = useTheme();
  const {
    field: { ref, onChange },
  } = useController({
    name,
    control,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [defaultValues] = useState({
    search: '',
  });

  const { control: controlSearch, setValue } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });

  const MenuProps = {
    PaperProps: {
      style: {
        backgroundColor: '#F1F1F1',
        color: theme.colors.secondary,
        maxHeight: 250,
      },
    },
    disableScrollLock: true,
  };

  const handleOpen = () => {
    setIsOpen(true);
    document.body.classList.add('no-scroll');
  };

  const handleClose = () => {
    setIsOpen(false);
    setValue('search', '');
    document.body.classList.remove('no-scroll');
  };

  const handleSearchClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (isOpen) {
      event.stopPropagation();
    }
  };
  return (
    <Container>
      <FormControl fullWidth>
        <Select
          open={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          displayEmpty
          classes={{ select: 'select-style' }}
          ref={ref}
          onChange={onChange}
          MenuProps={MenuProps}
          onKeyDown={(e) => e.stopPropagation()}
          IconComponent={ExpandArrowIcon}
          {...props}>
          {isSearchable && (
            <Grid display="flex" alignItems="center" justifyContent="center" justifyItems="center">
              <Grid width="90%">
                <Input
                  control={controlSearch}
                  name="search"
                  placeholder="Pesquisar..."
                  onClick={handleSearchClick}
                />
              </Grid>
            </Grid>
          )}
          {(approvers?.length || operators?.length || requesters?.length) && (
            <MenuItem
              disabled
              value=""
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '2.5rem',
                fontSize: '1.1rem',
              }}>
              {placeholderTitle}
            </MenuItem>
          )}
          <ListSubheader>Solicitante</ListSubheader>
          {requesters?.length ? (
            requesters.map((requester) => (
              <MenuItem
                key={uuid()}
                value={requester.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  width: '100%',
                  height: '2.5rem',
                  fontSize: '1.1rem',
                }}>
                <Tooltip disableInteractive title={requester.name} arrow>
                  <input
                    value={requester.name}
                    readOnly
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      width: '100%',
                      height: '1.5rem',
                      maxWidth: '12rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: theme.colors.secondary,
                      cursor: 'pointer',
                      outline: 'none',
                      fontSize: '1.1rem',
                      alignSelf: 'center',
                    }}
                  />
                </Tooltip>
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled style={{ justifyContent: 'center', alignItems: 'center' }}>
              Nenhum solicitante encontrado
            </MenuItem>
          )}
          <ListSubheader>Aprovador</ListSubheader>
          {approvers?.length ? (
            approvers.map((approver) => (
              <MenuItem
                key={uuid()}
                value={approver.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  width: '100%',
                  height: '2.5rem',
                  fontSize: '1.1rem',
                }}>
                <Tooltip disableInteractive title={approver.name} arrow>
                  <input
                    value={approver.name}
                    readOnly
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      width: '100%',
                      height: '1.5rem',
                      maxWidth: '12rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: theme.colors.secondary,
                      cursor: 'pointer',
                      outline: 'none',
                      fontSize: '1.1rem',
                      alignSelf: 'center',
                    }}
                  />
                </Tooltip>
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled style={{ justifyContent: 'center', alignItems: 'center' }}>
              Nenhum aprovador encontrado
            </MenuItem>
          )}
          <ListSubheader>Operador</ListSubheader>
          {operators?.length ? (
            operators.map((operator) => (
              <MenuItem
                key={uuid()}
                value={operator.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  width: '100%',
                  height: '2.5rem',
                  fontSize: '1.1rem',
                }}>
                <Tooltip disableInteractive title={operator.name} arrow>
                  <input
                    value={operator.name}
                    readOnly
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      width: '100%',
                      height: '1.5rem',
                      maxWidth: '12rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: theme.colors.secondary,
                      cursor: 'pointer',
                      outline: 'none',
                      fontSize: '1.1rem',
                      alignSelf: 'center',
                    }}
                  />
                </Tooltip>
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled style={{ justifyContent: 'center', alignItems: 'center' }}>
              Nenhum operador encontrado
            </MenuItem>
          )}
          <ListSubheader>Financeiro</ListSubheader>
          {financers?.length ? (
            financers.map((financer) => (
              <MenuItem
                key={uuid()}
                value={financer.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  width: '100%',
                  height: '2.5rem',
                  fontSize: '1.1rem',
                }}>
                <Tooltip disableInteractive title={financer.name} arrow>
                  <input
                    value={financer.name}
                    readOnly
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      width: '100%',
                      height: '1.5rem',
                      maxWidth: '12rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: theme.colors.secondary,
                      cursor: 'pointer',
                      outline: 'none',
                      fontSize: '1.1rem',
                      alignSelf: 'center',
                    }}
                  />
                </Tooltip>
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled style={{ justifyContent: 'center', alignItems: 'center' }}>
              Nenhum perfil financeiro encontrado
            </MenuItem>
          )}
        </Select>
      </FormControl>
      {errorText && (
        <span className="fieldError">
          <ErrorIcon fontSize="inherit" className="errorIcon" /> {errorText}
        </span>
      )}
    </Container>
  );
}
